import { baseApi } from '../api/baseApi';
import type {
    PasswordUnmaskRequest,
    PasswordUnmaskResponse,
    PasswordUnmaskSearchRequest,
    PasswordUnmaskSavedRequest,
    UnmaskAvailability
} from '../api/types';

// Helper function to validate unmask request
const validateUnmaskRequest = (data: PasswordUnmaskSavedRequest): UnmaskAvailability => {
    // Check if we have the required record_id
    if (!data.record_id) {
        return {
            canUnmask: false,
            reason: 'Missing record ID',
            context: 'saved'
        };
    }

    // Check if log_id is available (required for password unmasking)
    if (!data.log_id) {
        return {
            canUnmask: false,
            reason: 'This record type does not support password unmasking (missing log ID)',
            context: 'saved'
        };
    }

    return {
        canUnmask: true,
        context: 'saved'
    };
};

// Shared unmask API service - handles both search and saved contexts
export const unmaskApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // POST /intel/unmask-password/ - Unmask password for search results
        unmaskSearchPassword: builder.mutation<PasswordUnmaskResponse, PasswordUnmaskSearchRequest>({
            query: (data) => ({
                url: '/intel/unmask-password/',
                method: 'POST',
                body: data,
            }),
            // Don't cache unmask results
        }),

        // POST /intel/unmask-password/ - Unmask password for saved incidents
        // Now with better validation and error handling for optional log_id
        unmaskSavedPassword: builder.mutation<PasswordUnmaskResponse, PasswordUnmaskSavedRequest>({
            query: (data) => {
                // Validate the request before sending
                const validation = validateUnmaskRequest(data);
                if (!validation.canUnmask) {
                    // Return a rejected promise with a clear error
                    throw new Error(validation.reason || 'Cannot unmask password for this record');
                }

                // Only send the request if validation passes
                // Build the request body, only including log_id if it exists
                const requestBody: Record<string, any> = {
                    record_id: data.record_id
                };

                // Only include log_id if it's present and not null
                if (data.log_id !== null && data.log_id !== undefined) {
                    requestBody.log_id = data.log_id;
                }

                return {
                    url: '/intel/unmask-password/',
                    method: 'POST',
                    body: requestBody,
                };
            },
            transformErrorResponse: (response: any) => {
                // Enhanced error handling for saved incident unmasking
                const { status, data } = response;

                let errorMessage = 'Failed to unmask password. Please try again.';
                let errorCode = 'UNMASK_FAILED';

                if (status === 400) {
                    errorMessage = data?.details || 'Invalid request parameters';
                    errorCode = 'INVALID_REQUEST';
                } else if (status === 403) {
                    errorMessage = 'Unmask quota exceeded or not supported in your plan';
                    errorCode = 'QUOTA_EXCEEDED';
                } else if (status === 404) {
                    errorMessage = 'Record not found';
                    errorCode = 'RECORD_NOT_FOUND';
                } else if (status === 422) {
                    errorMessage = 'This record type does not support password unmasking';
                    errorCode = 'UNSUPPORTED_RECORD_TYPE';
                } else if (data?.error) {
                    errorMessage = data.error;
                    errorCode = data.code || 'API_ERROR';
                }

                return {
                    status,
                    data: {
                        success: false,
                        error: errorMessage,
                        code: errorCode,
                        details: data?.details
                    }
                };
            },
            // Don't cache unmask results
        }),

        // Generic unmask endpoint - determines context automatically
        unmaskPassword: builder.mutation<PasswordUnmaskResponse, PasswordUnmaskRequest>({
            query: (data) => ({
                url: '/intel/unmask-password/',
                method: 'POST',
                body: data,
            }),
            // Don't cache unmask results
        }),
    }),
    overrideExisting: false,
});

// Export validation helper for use in components
export { validateUnmaskRequest };

// Export hooks for usage in components
export const {
    useUnmaskSearchPasswordMutation,
    useUnmaskSavedPasswordMutation,
    useUnmaskPasswordMutation,
} = unmaskApi;